import React from 'react'

import {
  Box,
  DateInput,
  FormLabel,
  Grid,
  Input,
  RoundedPlainTextButtonMenu,
  makeAppStyles,
  RoundedPlainTextButton,
  CircularProgress,
  Dialog,
  PerceptAppBar,
  IconButton,
  Typography,
  DialogContent,
  DialogActions,
} from '@percept/mui'

import { ArrowDropDown, Check, Close } from '@percept/mui/icons'

import { Formik, FormikErrors } from 'formik'

import {
  CreateMediaAcademyResouceParams,
  useCreateMediaAcademyResource,
  useEditMediaAcademyResource,
} from '../hooks'

import { mediaAcademyResouceCategoryLabels } from '../lib'

import { Nullable } from '@percept/types'

import { MediaAcademyResource, MediaAcademyResourceCategory } from '../typings'
import { AdminDialogCloseHandler } from './typings'


type WebinarFormState = Nullable<CreateMediaAcademyResouceParams>

const initialValues: WebinarFormState = {
  name: null,
  external_url: null,
  category: null,
  published_date: null,
}


const useStyles = makeAppStyles( theme => ({
  inputContainer: {
    maxWidth: '30rem',
  },
  formLabel: {
    display: 'block',
    marginBottom: theme.spacing(1),
  },
}))


export type ManageResourceProps = {
  resource?: MediaAcademyResource | null
  onClose: AdminDialogCloseHandler
  onSuccess?: () => void
}

export const ManageResource = ({
  resource,
  onClose,
  onSuccess,
}: ManageResourceProps): JSX.Element => {
  const useResourceManagementHook = (
    resource ?
      useEditMediaAcademyResource :
      useCreateMediaAcademyResource
  )

  const query = useResourceManagementHook({
    onSuccess,
  })

  const classes = useStyles()

  const actionText = `${resource ? 'Edit' : 'Add'} Resource`

  return (
    <Formik
      initialValues={resource || initialValues}
      validate={(values): FormikErrors<WebinarFormState> => {
        const errors: FormikErrors<WebinarFormState> = {}
        const requiredFields: (keyof WebinarFormState)[] = [
          'category',
          'external_url',
          'name',
          'published_date',
        ]
        for( const field of requiredFields ){
          if( !values[field] ){
            errors[field] = `${field} is required`
          }
        }
        return errors
      }}
      onSubmit={(values): void => {
        query.mutate(values as MediaAcademyResource)
      }}>
      { ({ values, handleBlur, handleChange, setFieldValue, handleSubmit, isValid, dirty }): JSX.Element => (
        <form
          noValidate
          onSubmit={handleSubmit}>
          <Dialog
            open
            onClose={(): void => {
              onClose({ hasUnsavedChanges: dirty })
            }}
            fullWidth
            maxWidth='sm'>
            <PerceptAppBar
              color='primary'
              position='relative'>
              <Box display='flex' alignItems='center'>
                <Box mr={3}>
                  <IconButton
                    color='inherit'
                    onClick={(): void => {
                      onClose({ hasUnsavedChanges: dirty })
                    }}>
                    <Close />
                  </IconButton>
                </Box>
                <Typography variant='h4'>{actionText}</Typography>
              </Box>
            </PerceptAppBar>
            <DialogContent>
              <Grid container spacing={5}>
                <Grid item xs={12}>
                  <div className={classes.inputContainer}>
                    <FormLabel className={classes.formLabel}>Name</FormLabel>
                    <Input
                      fullWidth
                      autoFocus
                      name='name'
                      value={values.name}
                      onBlur={handleBlur}
                      onChange={handleChange} />
                  </div>
                </Grid>

                <Grid item xs={12}>
                  <div className={classes.inputContainer}>
                    <FormLabel className={classes.formLabel}>URL</FormLabel>
                    <Input
                      fullWidth
                      name='external_url'
                      value={values.external_url}
                      onBlur={handleBlur}
                      onChange={handleChange} />
                  </div>
                </Grid>

                <Grid item xs={12}>
                  <FormLabel className={classes.formLabel}>Category</FormLabel>
                  <RoundedPlainTextButtonMenu
                    TriggerProps={{
                      variant: 'contained',
                      color: 'secondary',
                      endIcon: <ArrowDropDown />,
                    }}
                    value={values.category}
                    label={
                      !values.category ?
                        'Select Category' :
                        mediaAcademyResouceCategoryLabels[values.category]
                    }
                    options={
                      (['PLAYBOOK', 'WHITEPAPER'] as MediaAcademyResourceCategory[]).map( value => ({
                        value,
                        label: mediaAcademyResouceCategoryLabels[value],
                      }))
                    }
                    onChange={(e, value): void => {
                      setFieldValue('category', value)
                    }} />
                </Grid>

                <Grid item xs={12}>
                  <FormLabel className={classes.formLabel}>Published Date</FormLabel>
                  <Box display='flex'>
                    <DateInput
                      value={values.published_date}
                      color='secondary'
                      variant='static'
                      format='dd/MM/yy'
                      onChange={(date): void => {
                        setFieldValue('published_date', date)
                      }} />
                  </Box>
                </Grid>

              </Grid>
            </DialogContent>

            <DialogActions>
              <Box pb={0.5} display='flex' alignItems='center'>
                <Box mr={2}>
                  <RoundedPlainTextButton
                    size='large'
                    variant='contained'
                    startIcon={<Close />}
                    onClick={(): void => {
                      onClose({ hasUnsavedChanges: dirty })
                    }}>
                Cancel
                  </RoundedPlainTextButton>
                </Box>

                <RoundedPlainTextButton
                  type='submit'
                  size='large'
                  variant='contained'
                  color='primary'
                  onClick={handleSubmit as unknown as React.MouseEventHandler}
                  startIcon={
                    query.isLoading ?
                      <CircularProgress size='1em' color='inherit' /> :
                      <Check />
                  }
                  disabled={!isValid || query.isLoading || !dirty}>
                  {actionText}
                </RoundedPlainTextButton>
              </Box>
            </DialogActions>
          </Dialog>
        </form>
      )}
    </Formik>
  )
}
